import { Gwei, ONE, ZERO } from './gwei';
import { add, divide, multiply, multiplyByDecimal, subtract } from './math';
import { calcPnl, isOppositeSide } from './positionHelpers';

export const calcEffectiveLeverage = ({
  totalPositionSize,
  totalSubaccountBalance,
}: {
  totalPositionSize: bigint | undefined;
  totalSubaccountBalance: bigint | undefined;
}) => {
  if (!totalPositionSize || !totalSubaccountBalance) {
    return undefined;
  }
  return divide(totalPositionSize, totalSubaccountBalance);
};

export const calcProjectedMaintenanceMargin = ({
  maintenanceMargin,
  currentPositionSize,
  newPositionSize,
  oraclePrice,
  maintenanceMarginRatio,
}: {
  maintenanceMargin: bigint | undefined;
  currentPositionSize: bigint | undefined;
  newPositionSize: bigint | undefined;
  oraclePrice: bigint | undefined;
  maintenanceMarginRatio: number;
}) => {
  if (
    maintenanceMargin === undefined ||
    oraclePrice === undefined ||
    currentPositionSize === undefined ||
    newPositionSize === undefined
  ) {
    return undefined;
  }

  const maintenanceMarginForCurrentPos = multiplyByDecimal(
    multiply(Gwei.abs(currentPositionSize), oraclePrice),
    maintenanceMarginRatio
  );
  const maintenanceMarginWithoutCurrentPos = subtract(
    maintenanceMargin,
    maintenanceMarginForCurrentPos
  );

  const maintenanceMarginForNewPos = multiplyByDecimal(
    multiply(Gwei.abs(newPositionSize), oraclePrice),
    maintenanceMarginRatio
  );
  return add(maintenanceMarginWithoutCurrentPos, maintenanceMarginForNewPos);
};

export function calcProjectedTotalBalance({
  subaccountTotalBalance,
  currentPositionSize,
  newPositionSize,
  avgEntryPrice,
  executionPrice,
  feeRate,
}: {
  subaccountTotalBalance?: bigint;
  currentPositionSize?: bigint;
  newPositionSize?: bigint;
  avgEntryPrice?: bigint;
  executionPrice?: bigint;
  feeRate?: bigint;
}) {
  if (
    subaccountTotalBalance === undefined ||
    currentPositionSize === undefined ||
    newPositionSize === undefined ||
    avgEntryPrice === undefined ||
    executionPrice === undefined ||
    feeRate === undefined
  ) {
    return undefined;
  }

  // No size change => no newly realized PnL
  if (currentPositionSize === newPositionSize) {
    return subaccountTotalBalance;
  }
  // If the old position was 0, it's a new position
  if (currentPositionSize === 0n) {
    return subaccountTotalBalance;
  }

  // If the new and old positions are on opposite sides,
  // it effectively closes the entire old position
  if (isOppositeSide(currentPositionSize, newPositionSize)) {
    const realizedPnl = calcPnl(executionPrice, avgEntryPrice, currentPositionSize);
    const fees = multiply(Gwei.abs(multiply(currentPositionSize, executionPrice)), feeRate);
    return subtract(add(subaccountTotalBalance, realizedPnl), fees);
  }

  // For same side, see if we are reducing or increasing
  const absDiff = Gwei.abs(currentPositionSize) - Gwei.abs(newPositionSize);

  // If absDiff <= 0, we are increasing => no realized PnL
  if (absDiff <= 0n) {
    return subaccountTotalBalance;
  }

  // Otherwise, we are partially closing. Respect the sign
  const signedReducingSize = currentPositionSize > 0n ? absDiff : -absDiff;
  const realizedPnl = calcPnl(executionPrice, avgEntryPrice, signedReducingSize);
  const fees = multiply(Gwei.abs(multiply(signedReducingSize, executionPrice)), feeRate);
  return subtract(add(subaccountTotalBalance, realizedPnl), fees);
}

/**
 * Calculates Initial Margin (IM) based on simple leverage formula:
 * IM = notionalValue / maxLeverage
 */
export function calcInitialMarginRequired(notionalValue: bigint, maxLeverage: number) {
  if (notionalValue <= 0) return 0n;
  return notionalValue / BigInt(maxLeverage);
}

/**
 * Calculates Maintenance Margin (MM) based on:
 * MM = 0.5 * IM = 0.5 * (notionalValue / maxLeverage)
 */
export function calcMaintenanceMargin(notionalValue: bigint, maxLeverage: number) {
  if (notionalValue <= 0) return 0n;
  return multiply(calcInitialMarginRequired(notionalValue, maxLeverage), Gwei.toGwei(0.5));
}

export interface CalcLiquidationPriceParams {
  oraclePrice: bigint;
  positionSize: bigint;
  accountEquity: bigint;
  /** Maintenance Margin Rate (MMR) as a decimal (e.g., 0.01 for 1%) */
  maintenanceMarginRatio: number;
}

/**
 * Calculates the liquidation price based on the standard margin trading formula:
 * LiqPrice = (oraclePrice * positionSize - accountEquity) / (positionSize * (1 +/- MMR))
 */
export function calcLiquidationPrice(params: CalcLiquidationPriceParams) {
  const { oraclePrice, positionSize, accountEquity, maintenanceMarginRatio } = params;

  if (positionSize === ZERO || oraclePrice <= ZERO) {
    return undefined;
  }

  const mmrBigint = Gwei.toGwei(maintenanceMarginRatio);
  const isLong = positionSize > ZERO;

  // --- Calculation ---

  const notional = multiply(oraclePrice, positionSize);
  const numerator = subtract(notional, accountEquity);
  const rateFactorBigint = isLong ? subtract(ONE, mmrBigint) : add(ONE, mmrBigint);
  const denominator = multiply(positionSize, rateFactorBigint);

  let liquidationPrice: bigint;
  try {
    // Calculate Final Liquidation Price: numerator / denominator
    liquidationPrice = divide(numerator, denominator);
  } catch (e) {
    console.error('Error during final division:', e);
    return undefined;
  }

  // --- Post-Calculation Adjustments ---

  // For long positions, liquidation price cannot be negative. Clamp to zero.
  if (isLong && liquidationPrice < ZERO) {
    return ZERO;
  }

  // For short positions, if the calculation results in a non-positive price,
  // it might imply liquidation is impossible
  if (!isLong && liquidationPrice <= ZERO) {
    return undefined;
  }

  return liquidationPrice;
}

/**
 * Helper to calculate the percentage distance to liquidation
 * Returns:
 * - Positive value for short positions (price needs to move up to get closer to liquidation)
 * - Negative value for long positions (price needs to move down to get closer to liquidation)
 * - undefined if either price is <= 0
 */
export function calcDistanceToLiquidation({
  oraclePrice,
  liquidationPrice,
}: {
  oraclePrice: bigint;
  liquidationPrice: bigint;
}) {
  if (oraclePrice <= 0 || liquidationPrice <= 0) {
    return undefined;
  }

  const priceDiff = oraclePrice - liquidationPrice;
  // Calculate base distance
  const distance = divide(priceDiff, oraclePrice);
  return multiply(Gwei.toGwei(-1), distance);
}
